/**
 * Bubble factory functions for creating bubble objects from content blocks.
 * Centralizes bubble creation logic to ensure consistency.
 */

import type { ContentBlock } from "@/lib/tools/schemas";
import type { Bubble } from "../streaming/reducer";
import { parsePlanTasks } from "../utils/todo-utils";
import { normalizeToolInput } from "./normalizer";

interface BubbleContext {
	messageId: string;
	baseTimestamp: Date;
	blockIndex: number;
}

export interface ToolCallMetadata {
	tool_id?: string;
	tool_name?: string;
	tool_input?: unknown;
	result?: unknown;
}

interface BubbleFactoryOptions {
	toolCallMeta?: ToolCallMetadata;
}

function withBlockId(
	metadata: Bubble["metadata"],
	blockId: string | undefined
): Bubble["metadata"] | undefined {
	if (!blockId) {
		return metadata;
	}
	return {
		...(metadata || {}),
		blockId,
	};
}

/**
 * Create a bubble from a content block with consistent structure.
 */
export function createBubbleFromContentBlock(
	block: ContentBlock,
	context: BubbleContext,
	options: BubbleFactoryOptions = {}
): Bubble | null {
	const { messageId, baseTimestamp, blockIndex } = context;
	const timestamp = new Date(baseTimestamp.getTime() + blockIndex * 100);
	const bubbleId = `assistant-${messageId}-block-${blockIndex}`;
	const contextBlockId = (block as { id?: string }).id || `${messageId}:${blockIndex}`;

	switch (block.type) {
		case "text":
			return {
				id: bubbleId,
				type: "text",
				status: "complete",
				content: block.text || "",
				metadata: withBlockId(undefined, contextBlockId),
				timestamp,
			};

		case "thinking":
			return {
				id: bubbleId,
				type: "thinking",
				status: "complete",
				content: block.thinking || "",
				metadata: withBlockId(
					{
						signature: block.signature,
					},
					contextBlockId
				),
				timestamp,
			};

		case "reasoning":
			return {
				id: bubbleId,
				type: "reasoning",
				status: "complete",
				content: block.reasoning || "",
				metadata: withBlockId(undefined, contextBlockId),
				timestamp,
			};

		case "plan": {
			const planText = block.plan || "";
			const tasks = parsePlanTasks(planText);

			return {
				id: bubbleId,
				type: "todo",
				status: "complete",
				content: planText,
				metadata: withBlockId({ tasks }, contextBlockId),
				timestamp,
			};
		}

		case "tool_use": {
			const toolInput = normalizeToolInput(block.name, block.input);

			return {
				id: bubbleId,
				type: "tool_call",
				status: "complete",
				content: JSON.stringify(toolInput, null, 2),
				metadata: withBlockId(
					{
						toolName: block.name,
						toolInput,
						toolId: block.id,
					},
					block.id
				),
				timestamp,
			};
		}

		case "tool_result": {
			const toolCallMeta = options.toolCallMeta;
			let toolResultContent: string;
			if (typeof block.content === "string" && block.content.trim().length > 0) {
				toolResultContent = block.content;
			} else if (toolCallMeta?.result !== undefined) {
				toolResultContent =
					typeof toolCallMeta.result === "string"
						? toolCallMeta.result
						: JSON.stringify(toolCallMeta.result);
			} else {
				toolResultContent = JSON.stringify(block.content ?? "");
			}

			let toolInput: Record<string, unknown> | undefined;
			const rawInput = toolCallMeta?.tool_input;
			const toolName = toolCallMeta?.tool_name || "";
			if (rawInput && typeof rawInput === "object" && !Array.isArray(rawInput)) {
				toolInput = normalizeToolInput(toolName, rawInput as Record<string, unknown>);
			} else if (typeof rawInput === "string") {
				try {
					const parsed = JSON.parse(rawInput);
					if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
						toolInput = normalizeToolInput(toolName, parsed as Record<string, unknown>);
					}
				} catch {
					// ignore parse errors
				}
			}

			return {
				id: bubbleId,
				type: "tool_result",
				status: "complete",
				content: toolResultContent,
				metadata: withBlockId(
					{
						toolId: block.tool_use_id,
						isError: block.is_error,
						toolName: toolCallMeta?.tool_name,
						toolInput,
					},
					block.tool_use_id ? `tool_result:${block.tool_use_id}` : undefined
				),
				timestamp,
			};
		}

		default:
			// Unknown block type
			return null;
	}
}

/**
 * Create a user message bubble.
 */
export function createUserBubble(messageId: string, content: string, timestamp: Date): Bubble {
	return {
		id: `user-${messageId}`,
		type: "user",
		status: "complete",
		content,
		timestamp,
	};
}

/**
 * Create a generic bubble with basic properties.
 * Useful for streaming reducer and other dynamic bubble creation.
 */
export function createBubble(
	type: Bubble["type"],
	content: string,
	metadata?: Bubble["metadata"],
	status: Bubble["status"] = "complete"
): Bubble {
	return {
		id: `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
		type,
		status,
		content,
		metadata,
		timestamp: new Date(),
	};
}
